import Link from 'next/link'
import Image from 'next/image'
import { useRouter } from 'next/router'
import MonoforFooterLogo from './icons/monofor-footer-logo'
import NewsletterSubscribeForm from '@comp/newsletter-subscribe-form'
import Container from '@comp/ui/container'
import Text from '@comp/ui/text'
import { useGlobalContext } from '../context/GlobalContext'

const year = new Date().getFullYear()

const productNavExtras = [
  { name: 'Monosign', href: '/sign' },
  { name: 'Monopam', href: '/pam' },
  { name: 'Monosync', href: '/sync' },
]

const solutionsNav = [
  { label: { en: 'AI agent security', tr: 'AI ajan güvenliği' }, href: '/solutions/ai-security' },
  { label: { en: 'Machine identity', tr: 'Makine kimliği' }, href: '/solutions/machine-identity' },
  { label: { en: 'Identity security posture', tr: 'Kimlik güvenliği duruşu' }, href: '/solutions/identity-security-posture' },
  { label: { en: 'DevOps privileged access', tr: 'DevOps ayrıcalıklı erişimi' }, href: '/solutions/devops-privileged-access' },
  { label: { en: 'Zero Trust', tr: 'Sıfır Güven' }, href: '/solutions/zero-trust' },
  { label: { en: 'Joiner / Mover / Leaver', tr: 'İşe Giriş / Geçiş / Ayrılış' }, href: '/solutions/joiner-mover-leaver' },
  { label: { en: 'Passwordless & MFA', tr: 'Parolasız ve MFA' }, href: '/solutions/passwordless-mfa' },
  { label: { en: 'Audit & compliance', tr: 'Denetim ve uyum' }, href: '/solutions/audit-compliance' },
  { label: { en: 'External & partner access', tr: 'Dış ve iş ortağı erişimi' }, href: '/solutions/external-partners' },
  { label: { en: 'KVKK compliance', tr: 'KVKK uyumu' }, href: '/compliance/kvkk' },
  { label: { en: 'DORA compliance', tr: 'DORA uyumu' }, href: '/compliance/dora' },
  { label: { en: 'GDPR compliance', tr: 'GDPR uyumu' }, href: '/compliance/gdpr' },
  { label: { en: 'NIS2 compliance', tr: 'NIS2 uyumu' }, href: '/compliance/nis2' },
  { label: { en: 'IEC 62443 compliance', tr: 'IEC 62443 uyumu' }, href: '/compliance/iec-62443' },
]

const industriesNav = [
  { label: { en: 'Financial Services', tr: 'Finansal Hizmetler' }, href: '/solutions/industries/financial-services' },
  { label: { en: 'Healthcare', tr: 'Sağlık' }, href: '/solutions/industries/healthcare' },
  { label: { en: 'Manufacturing', tr: 'Üretim' }, href: '/solutions/industries/manufacturing' },
  { label: { en: 'Public Sector', tr: 'Kamu' }, href: '/solutions/industries/public-sector' },
  { label: { en: 'Retail', tr: 'Perakende' }, href: '/solutions/industries/retail' },
  { label: { en: 'Telecommunications', tr: 'Telekomünikasyon' }, href: '/solutions/industries/telco' },
]

const resourceNav = [
  { label: { en: 'Integrations', tr: 'Entegrasyonlar' }, href: '/integrations', external: false },
  { label: { en: 'Learn', tr: 'Öğrenin' }, href: '/learn', external: false },
  { label: { en: 'Glossary', tr: 'Sözlük' }, href: '/glossary', external: false },
  { label: { en: 'PAM TCO Calculator', tr: 'PAM TCO Hesaplayıcı' }, href: '/tools/pam-tco-calculator', external: false },
  { label: { en: 'Compliance Checklist', tr: 'Uyum Kontrol Listesi' }, href: '/tools/identity-compliance-checklist', external: false },
  { label: { en: 'AD Service Account Scanner', tr: 'AD Servis Hesabı Tarayıcı' }, href: '/tools/ad-service-account-scanner', external: false },
  { label: { en: 'Blog', tr: 'Blog' }, href: '/blog', external: false },
  { label: { en: 'Videos', tr: 'Videolar' }, href: 'https://www.youtube.com/@monofor', external: true },
  { label: { en: 'Documentation', tr: 'Dokümantasyon' }, href: 'https://help.monofor.com', external: true },
  { label: { en: 'Press kit', tr: 'Basın kiti' }, href: '/press-kit', external: false },
]

const compareNav = [
  { label: { en: 'All comparisons', tr: 'Tüm karşılaştırmalar' }, href: '/compare' },
  { label: { en: 'Okta alternative', tr: 'Okta alternatifi' }, href: '/compare/okta-alternative' },
  { label: { en: 'CyberArk alternative', tr: 'CyberArk alternatifi' }, href: '/compare/cyberark-alternative' },
  { label: { en: 'SailPoint alternative', tr: 'SailPoint alternatifi' }, href: '/compare/sailpoint-alternative' },
  { label: { en: 'Delinea alternative', tr: 'Delinea alternatifi' }, href: '/compare/delinea-alternative' },
  { label: { en: 'BeyondTrust alternative', tr: 'BeyondTrust alternatifi' }, href: '/compare/beyondtrust-alternative' },
  { label: { en: 'ADFS alternative', tr: 'ADFS alternatifi' }, href: '/compare/adfs-alternative' },
  { label: { en: 'Keycloak alternative', tr: 'Keycloak alternatifi' }, href: '/compare/keycloak-alternative' },
  { label: { en: 'Venafi alternative', tr: 'Venafi alternatifi' }, href: '/compare/venafi-alternative' },
  { label: { en: 'Teleport alternative', tr: 'Teleport alternatifi' }, href: '/compare/teleport-alternative' },
]

const personaNav = [
  { label: { en: 'IT admins', tr: 'BT yöneticileri' }, href: '/for/it-admins' },
  { label: { en: 'Security engineers', tr: 'Güvenlik mühendisleri' }, href: '/for/security-engineers' },
  { label: { en: 'Developers', tr: 'Geliştiriciler' }, href: '/for/developers' },
  { label: { en: 'Compliance officers', tr: 'Uyum sorumluları' }, href: '/for/compliance-officers' },
  { label: { en: 'End users', tr: 'Son kullanıcılar' }, href: '/for/end-users' },
]

const companyNav = [
  { label: { en: 'About Us', tr: 'Hakkımızda' }, href: '/company', external: false },
  { label: { en: 'Pricing', tr: 'Fiyatlandırma' }, href: '/pricing', external: false },
  { label: { en: 'Become a Partner', tr: 'İş Ortağı Olun' }, href: '/become-partner', external: false },
  { label: { en: 'Sustainability', tr: 'Sürdürülebilirlik' }, href: '/legal/information-security-policy', external: false },
]

const columnTitles = {
  product: { en: 'Product', tr: 'Ürün' },
  solutions: { en: 'Solutions', tr: 'Çözümler' },
  industries: { en: 'Industries', tr: 'Sektörler' },
  resources: { en: 'Resources', tr: 'Kaynaklar' },
  compare: { en: 'Compare', tr: 'Karşılaştır' },
  forYou: { en: 'For you', tr: 'Sizin için' },
  company: { en: 'Company', tr: 'Şirket' },
}

const footerCopy = {
  contactPre: {
    en: 'To connect with a product expert today,',
    tr: 'Bugün bir ürün uzmanıyla görüşmek için',
  },
  contactLink: {
    en: 'fill our contact form',
    tr: 'iletişim formumuzu',
  },
  contactPost: {
    en: '.',
    tr: ' doldurun.',
  },
  contactUs: { en: 'Contact Us', tr: 'Bize Ulaşın' },
  newsletterTitle: { en: 'Join our newsletter', tr: 'Bültenimize katılın' },
  newsletterBlurb: {
    en: "Don't miss any updates from Monofor.",
    tr: "Monofor'dan güncellemeleri kaçırmayın.",
  },
  rights: {
    en: `© ${year} Monofor, Inc. All rights reserved.`,
    tr: `© ${year} Monofor, Inc. Tüm hakları saklıdır.`,
  },
  terms: { en: 'Terms and Conditions', tr: 'Şartlar ve Koşullar' },
  privacy: { en: 'Privacy Policy', tr: 'Gizlilik Politikası' },
}

const socials = [
  {
    label: 'X',
    href: 'https://www.twitter.com/monoforINC',
    icon: TwitterIcon,
  },
  {
    label: 'Instagram',
    href: 'https://instagram.com/monoforinc',
    icon: InstagramIcon,
  },
  {
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/company/monofor/',
    icon: LinkedInIcon,
  },
]

export default function SiteFooter() {
  const { locale } = useRouter()
  const lang = locale === 'tr' ? 'tr' : 'en'
  const { globalData } = useGlobalContext()
  const features = globalData?.features ?? []

  return (
    <footer className="bg-surface-inverse text-ink-inverse">
      <Container size="xl" className="pt-20 pb-12 lg:pt-24 lg:pb-16">
        <div className="grid gap-12 lg:grid-cols-[1.4fr_repeat(4,_1fr)]">
          {/* Brand + contact */}
          <div>
            <div className="text-white">
              <MonoforFooterLogo />
            </div>
            <Text size="md" className="mt-6 max-w-xs !text-brand-100">
              {footerCopy.contactPre[lang]}{' '}
              <Link href="/contact" className="text-white underline-offset-4 hover:underline">
                {footerCopy.contactLink[lang]}
              </Link>
              {footerCopy.contactPost[lang]}
            </Text>
            <a
              href="mailto:sales@monofor.com"
              className="mt-6 inline-flex items-center justify-center rounded-pill bg-white px-5 py-2.5 text-body-sm font-medium text-brand-900 transition hover:bg-brand-50"
            >
              {footerCopy.contactUs[lang]}
            </a>
          </div>

          {/* Product + For you stacked */}
          <div className="space-y-12">
            <FooterColumn title={columnTitles.product[lang]}>
              {productNavExtras.map((p) => (
                <FooterLink key={p.name} href={p.href}>{p.name}</FooterLink>
              ))}
              {features.length > 0 && (
                <div className="mt-4 border-t border-white/10 pt-4 space-y-2">
                  {features.slice(0, 6).map((f: any) => (
                    <FooterLink key={f.slug} href={'/features/' + f.slug}>
                      {f.title}
                    </FooterLink>
                  ))}
                </div>
              )}
            </FooterColumn>
            <FooterColumn title={columnTitles.forYou[lang]}>
              {personaNav.map((p) => (
                <FooterLink key={p.label.en} href={p.href}>{p.label[lang]}</FooterLink>
              ))}
            </FooterColumn>
          </div>

          {/* Solutions */}
          <FooterColumn title={columnTitles.solutions[lang]}>
            {solutionsNav.map((s) => (
              <FooterLink key={s.label.en} href={s.href}>{s.label[lang]}</FooterLink>
            ))}
          </FooterColumn>

          {/* Industries + Compare stacked */}
          <div className="space-y-12">
            <FooterColumn title={columnTitles.industries[lang]}>
              {industriesNav.map((i) => (
                <FooterLink key={i.label.en} href={i.href}>{i.label[lang]}</FooterLink>
              ))}
            </FooterColumn>
            <FooterColumn title={columnTitles.compare[lang]}>
              {compareNav.map((c) => (
                <FooterLink key={c.label.en} href={c.href}>{c.label[lang]}</FooterLink>
              ))}
            </FooterColumn>
          </div>

          {/* Resources + Company stacked */}
          <div className="space-y-12">
            <FooterColumn title={columnTitles.resources[lang]}>
              {resourceNav.map((r) =>
                r.external ? (
                  <FooterExternal key={r.label.en} href={r.href}>{r.label[lang]}</FooterExternal>
                ) : (
                  <FooterLink key={r.label.en} href={r.href}>{r.label[lang]}</FooterLink>
                )
              )}
            </FooterColumn>
            <FooterColumn title={columnTitles.company[lang]}>
              {companyNav.map((c) => (
                <FooterLink key={c.label.en} href={c.href}>{c.label[lang]}</FooterLink>
              ))}
            </FooterColumn>
          </div>
        </div>

        {/* Newsletter */}
        <div className="mt-20 grid items-end gap-8 border-t border-white/10 pt-12 lg:grid-cols-[1fr_auto] lg:gap-16">
          <div>
            <h6 className="text-display-sm font-medium text-white">
              {footerCopy.newsletterTitle[lang]}
            </h6>
            <Text size="md" className="mt-2 text-brand-200/90">
              {footerCopy.newsletterBlurb[lang]}
            </Text>
          </div>
          <div className="w-full max-w-md">
            <NewsletterSubscribeForm />
          </div>
        </div>

        {/* Sub-footer */}
        <div className="mt-16 flex flex-col gap-8 border-t border-white/10 pt-8 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:gap-8">
            <div className="flex items-center gap-3">
              {socials.map(({ label, href, icon: Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={label}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full text-white/70 transition hover:bg-white/10 hover:text-white"
                >
                  <Icon />
                </a>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-body-sm text-brand-200/90">
              <span>{footerCopy.rights[lang]}</span>
              <Link
                href="/legal/terms-and-service"
                className="transition hover:text-white"
                target="_blank"
              >
                {footerCopy.terms[lang]}
              </Link>
              <Link
                href="/legal/privacy-policy"
                className="transition hover:text-white"
                target="_blank"
              >
                {footerCopy.privacy[lang]}
              </Link>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Image
              src="/images/iso-27001.png"
              width={64}
              height={64}
              alt="Monofor is ISO 27001 Certified"
              className="brightness-110"
            />
          </div>
        </div>
      </Container>
    </footer>
  )
}

function FooterColumn({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <div>
      <div className="text-eyebrow font-semibold uppercase text-brand-200/90">
        {title}
      </div>
      <ul className="mt-5 space-y-2">{children}</ul>
    </div>
  )
}

function FooterLink({
  href,
  children,
}: {
  href: string
  children: React.ReactNode
}) {
  return (
    <li>
      <Link
        href={href}
        className="text-body-sm text-brand-200/90 transition hover:text-white"
      >
        {children}
      </Link>
    </li>
  )
}

function FooterExternal({
  href,
  children,
}: {
  href: string
  children: React.ReactNode
}) {
  return (
    <li>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-body-sm text-brand-200/90 transition hover:text-white"
      >
        {children}
      </a>
    </li>
  )
}

function TwitterIcon() {
  // X (formerly Twitter) wordmark. Square viewBox keeps it visually aligned
  // with the other 18×18 social glyphs in the row.
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 1200 1227"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M714.163 519.284 1160.89 0h-105.86L667.137 450.887 357.328 0H0l468.492 681.821L0 1226.37h105.866l409.625-476.152 327.181 476.152H1200L714.137 519.284h.026ZM569.165 687.828l-47.468-67.894-377.686-540.24h162.604l304.797 435.991 47.468 67.894 396.2 566.721H892.476L569.165 687.854v-.026Z"
        fill="currentColor"
      />
    </svg>
  )
}

function InstagramIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2.16c3.2 0 3.58.01 4.85.07 3.25.15 4.77 1.7 4.92 4.92.06 1.27.07 1.65.07 4.85 0 3.2-.01 3.58-.07 4.85-.15 3.22-1.67 4.77-4.92 4.92-1.27.06-1.65.07-4.85.07-3.2 0-3.58-.01-4.85-.07-3.26-.15-4.77-1.7-4.92-4.92C2.18 15.58 2.16 15.2 2.16 12c0-3.2.02-3.58.07-4.85.15-3.23 1.67-4.77 4.92-4.92C8.42 2.17 8.8 2.16 12 2.16zM12 0C8.74 0 8.33.01 7.05.07 2.7.27.27 2.69.07 7.05.01 8.33 0 8.74 0 12s.01 3.67.07 4.95C.27 21.3 2.69 23.73 7.05 23.93c1.28.06 1.69.07 4.95.07s3.67-.01 4.95-.07c4.35-.2 6.78-2.62 6.98-6.98.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95C23.73 2.7 21.31.27 16.95.07 15.67.01 15.26 0 12 0zm0 5.84A6.16 6.16 0 1 0 18.16 12 6.16 6.16 0 0 0 12 5.84zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.41-11.85a1.44 1.44 0 1 0 0 2.88 1.44 1.44 0 0 0 0-2.88z" fill="currentColor" />
    </svg>
  )
}

function LinkedInIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M19 0H5C2.24 0 0 2.24 0 5v14c0 2.76 2.24 5 5 5h14c2.76 0 5-2.24 5-5V5c0-2.76-2.24-5-5-5zM8 19H5V8h3v11zM6.5 6.73a1.76 1.76 0 1 1 0-3.53 1.76 1.76 0 0 1 0 3.53zM20 19h-3v-5.6c0-3.37-4-3.11-4 0V19h-3V8h3v1.77c1.4-2.59 7-2.78 7 2.47V19z" fill="currentColor" />
    </svg>
  )
}
